import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { TextField, Button, Snackbar, Alert, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { POST_CUSTOM_FORM_WITHOUT_LOGIN } from '../../commons/api_url';
import { Portal } from '@mui/material';

export default function DynamicForm({
                                        item,
                                        businessAccountId,
                                        projectId,
                                        globalparams
                                    }) {
    const { schemaType, id: formId, elements = [] } = item.uiElements;

    /* ------------------------------------------------------------------ */
    /* State and helpers                                                 */
    /* ------------------------------------------------------------------ */

    const initialValues = useMemo(() => {
        const obj = {};
        elements
            .filter(el => el.type === 'input')
            .forEach(el => {
                obj[el.key] = el.defaultValue ?? '';
            });
        return obj;
    }, [formId]);

    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});

    // Corrected Snackbar state management
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        // Resetting state on close is key
        setSnackbar(prev => ({ ...prev, open: false }));
    };


    useEffect(() => setValues(initialValues), [initialValues]);

    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setValues(v => ({ ...v, [key]: value }));
            if (errors[key]) {
                setErrors(err => ({ ...err, [key]: '' }));
            }
        },
        [errors] // Added dependency
    );

    /* ------------------------------------------------------------------ */
    /* Submit                                                            */
    /* ------------------------------------------------------------------ */

    const handleSubmit = async () => {
        const newErrors = {};
        elements
            .filter(el => el.type === 'input' && el.required)
            .forEach(el => {
                if (!values[el.key]?.trim()) {
                    newErrors[el.key] = `"${el.label}" is required`;
                }
            });

        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        try {
            await axios.post(
                POST_CUSTOM_FORM_WITHOUT_LOGIN,
                values,
                { params: { businessAccountId, projectId, formId } }
            );
            showSnackbar('Form submitted successfully!', 'success');
            setValues(initialValues);
            setErrors({}); // Also clear errors on success
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Error submitting form.', 'error'); // Use 'error' severity
        }
    };

    if (schemaType !== 'form') return null;

    /* ------------------------------------------------------------------ */
    /* Styling helpers                                                   */
    /* ------------------------------------------------------------------ */

    const buttonColor = globalparams?.userMessageBg ?? '#1976d2';
    const textColor   = globalparams?.userMessageText ?? '#fff';

    /* ------------------------------------------------------------------ */
    /* Render                                                            */
    /* ------------------------------------------------------------------ */

    return (
        <>
            <Stack spacing={2} sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                {elements.map(el => {
                    if (el.type === 'input') {
                        return (
                            <TextField
                                key={el.key}
                                id={el.key}
                                label={el.label}
                                fullWidth
                                multiline
                                required={el.required}
                                variant="outlined"
                                value={values[el.key] ?? ''}
                                onChange={handleChange(el.key)}
                                error={Boolean(errors[el.key])}
                                helperText={errors[el.key] || ' '}
                                sx={{
                                    typography: 'body2',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '& fieldset': { borderRadius: 2 },
                                        '&.Mui-focused fieldset': { borderColor: buttonColor }
                                    },
                                    '& label.Mui-focused': { color: buttonColor }
                                }}
                            />
                        );
                    }

                    if (el.type === 'button') {
                        return (
                            <Button
                                key={el.key}
                                fullWidth
                                variant="contained"
                                onClick={handleSubmit}
                                sx={{
                                    typography: 'body2',
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 2,
                                    color: textColor,
                                    backgroundColor: buttonColor,
                                    '&:hover': { backgroundColor: alpha(buttonColor, 0.85) },
                                }}
                            >
                                {el.label}
                            </Button>
                        );
                    }

                    return null;
                })}
            </Stack>
            <Portal>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {/* The Alert component now correctly reads the message and severity */}
                {/* from the state each time it opens. */}
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            </Portal>
        </>
    );
}